import { useEffect, useRef } from 'react';

import { useDialogHost } from '@/hooks/useDialogHost';
import { useToast } from '@/hooks/useToast';
import { useTranslation } from '@/hooks/useTranslation';
import {
    compareKeysFromLocation,
    type CompareRouteKeys,
    leaveCompareUrl,
} from '@/pages/browse/browseRoute';
import { getInjector } from '@/services/angularjs-bridge';
import { fetchItemData } from '@/services/utils';

function readCompareKeys(): CompareRouteKeys | null {
    try {
        const injector = getInjector();
        const $location = injector.get('$location');
        const path = typeof $location.path === 'function' ? String($location.path() || '') : '';
        const params = injector.get('$routeParams');
        return compareKeysFromLocation(path, params);
    } catch {
        return null;
    }
}

/**
 * Deep link `/compare/:first/:second` opens {@link CompareItemsDialog} on
 * the browse shell. The URL stays until the dialog closes, so a reload
 * opens it again. Closing switches the route back to `/browse`.
 */
const useOpenCompareFromUrl = () => {
    const dialogHost = useDialogHost();
    const { toast } = useToast();
    const { t } = useTranslation();
    const dialogHostRef = useRef(dialogHost);
    const toastRef = useRef(toast);
    const tRef = useRef(t);
    dialogHostRef.current = dialogHost;
    toastRef.current = toast;
    tRef.current = t;

    useEffect(() => {
        const keys = readCompareKeys();
        if (!keys) {
            return undefined;
        }
        let cancelled = false;
        const { firstPrimaryKey, secondPrimaryKey } = keys;
        void (async () => {
            const items = await Promise.all([
                fetchItemData(firstPrimaryKey),
                fetchItemData(secondPrimaryKey),
            ]);
            if (cancelled) {
                return;
            }
            if (items[0] == null || items[1] == null) {
                toastRef.current.error(tRef.current('ERROR_TECHNICAL'));
                return;
            }
            dialogHostRef.current.openCompare(items, {
                currentLayout: 'browse',
                onClosed: () => leaveCompareUrl(),
            });
        })();
        return () => {
            cancelled = true;
        };
    }, []);
};

export { useOpenCompareFromUrl };
